/**
 * oct20a S1 — the two server calls behind the voluntary diagnostic evidence UI.
 * Never infers anything: the server decides who may record what, and when.
 */
import { supabase } from '@/integrations/supabase/client';
import type { DiagnosticOutcome } from '@/types/diagnostic';
import type { EvidencePhase } from './evidence';

type Rpc = (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
const rpc: Rpc = (fn, args) => (supabase.rpc as unknown as Rpc)(fn, args);

export interface EvidenceTest { testName: string; result: string | null; values: unknown; notes: string | null }

export interface EvidenceEntry {
  entryId: string;
  kind: 'INTERIM_EVIDENCE' | 'SESSION_CONCLUSION';
  jobId: string;
  jobName: string;
  technicianName: string;
  isMine: boolean;
  recordedAt: string;
  phase: EvidencePhase;
  sessionDisposition: DiagnosticOutcome | null;
  confidence: number | null;
  findings: string | null;
  reasoning: string | null;
  unresolvedQuestions: string | null;
  /** OCT28 #2: the technician's recommended repair (not an approved job). */
  recommendation: string | null;
  tests: EvidenceTest[];
}

export interface DiagnosticJobOutcome {
  jobId: string;
  jobName: string;
  /** The job-level outcome — the only "final" — or null while the diagnosis is open. */
  diagnosisOutcome: DiagnosticOutcome | null;
  myPhase: EvidencePhase | null;
}

export interface DiagnosticEvidenceTimeline {
  entries: EvidenceEntry[];
  jobs: DiagnosticJobOutcome[];
  /** Jobs the caller may add evidence to right now. */
  recordableJobIds: string[];
}

const str = (v: unknown) => (v === null || v === undefined ? null : String(v));

export function parseEvidenceTimeline(data: unknown): DiagnosticEvidenceTimeline | null {
  const r = (data ?? {}) as Record<string, unknown>;
  if (r.outcome !== 'OK') return null;
  const entries = ((r.entries as Array<Record<string, unknown>>) ?? []).map((e): EvidenceEntry => ({
    entryId: String(e.entry_id),
    kind: e.entry_kind === 'SESSION_CONCLUSION' ? 'SESSION_CONCLUSION' : 'INTERIM_EVIDENCE',
    jobId: String(e.job_id),
    jobName: String(e.job_name ?? 'Diagnostic'),
    technicianName: String(e.technician_name ?? 'Technician'),
    isMine: e.is_mine === true,
    recordedAt: String(e.recorded_at),
    phase: (e.recorded_phase as EvidencePhase) ?? 'RUNNING',
    sessionDisposition: (e.session_disposition as DiagnosticOutcome | null) ?? null,
    confidence: typeof e.confidence === 'number' ? e.confidence : null,
    findings: str(e.findings),
    reasoning: str(e.reasoning),
    unresolvedQuestions: str(e.unresolved_questions),
    recommendation: str(e.recommendation),
    tests: ((e.tests as Array<Record<string, unknown>>) ?? []).map((t) => ({
      testName: String(t.test_name), result: str(t.result), values: t.values ?? null, notes: str(t.notes),
    })),
  }));
  const jobs = ((r.diagnostic_jobs as Array<Record<string, unknown>>) ?? []).map((j): DiagnosticJobOutcome => ({
    jobId: String(j.job_id),
    jobName: String(j.job_name ?? 'Diagnostic'),
    diagnosisOutcome: (j.diagnosis_outcome as DiagnosticOutcome | null) ?? null,
    myPhase: (j.my_phase as EvidencePhase | null) ?? null,
  }));
  return { entries, jobs, recordableJobIds: ((r.recordable_job_ids as unknown[]) ?? []).map(String) };
}

export async function readDiagnosticEvidenceTimeline(ticketId: string): Promise<DiagnosticEvidenceTimeline | null> {
  const { data, error } = await rpc('diagnostic_evidence_timeline', { p_ticket_id: ticketId });
  if (error) {
    console.warn('[readDiagnosticEvidenceTimeline] failed', error);
    return null;
  }
  return parseEvidenceTimeline(data);
}

export type RecordEvidenceResult =
  | { ok: true; entryId: string; phase: EvidencePhase }
  | { ok: false; outcome: string; reason?: string };

export async function recordMyDiagnosticEvidence(
  ticketId: string, jobId: string, evidence: Record<string, unknown>, requestId: string,
): Promise<RecordEvidenceResult> {
  const { data, error } = await rpc('record_my_diagnostic_evidence', {
    p_ticket_id: ticketId, p_job_id: jobId, p_evidence: evidence, p_request_id: requestId,
  });
  if (error) return { ok: false, outcome: 'FAILED', reason: error.message };
  const r = (data ?? {}) as Record<string, unknown>;
  if (r.outcome === 'EVIDENCE_RECORDED' || r.outcome === 'ALREADY_RECORDED') {
    return { ok: true, entryId: String(r.entry_id), phase: (r.recorded_phase as EvidencePhase) ?? 'RUNNING' };
  }
  return { ok: false, outcome: String(r.outcome ?? 'FAILED'), reason: r.reason ? String(r.reason) : undefined };
}
